import {
  toEngagedTargetIconFeature,
  toEngagementCircleFeature,
} from '@components/map/layers/engagement/engagement.mapper';
import { toFlightPlanFillFeatures } from '@components/map/layers/flightPlans/operational/flightPlan.mapper';
import { toActivePoiFeatures } from '@components/map/layers/poi/poi.mapper';
import {
  toPolygonLatestOutlineFeatures,
  toPolygonLocationFeatures,
  toPolygonTrajectoryOutlineFeatures,
} from '@components/map/layers/polygons/polygon.mapper';
import { toStrobeAzimuthLineFeatures, toStrobeSectorFeatures } from '@components/map/layers/strobes/strobe.mapper';
import {
  toExpiredDroneIconFeature,
  toExpiredDroneTrajectoryFeatures,
  toTargetCrossFeature,
  toTargetFlightPlansLinkFeatures,
  toTargetIconFeatures,
  toTargetLinkFeatures,
  toTargetSpeedFeatures,
  toTargetTrajectoryFeatures,
} from '@components/map/layers/targets/target.mapper';
import {
  toIMZFeatures,
  toIMZMarginFeatures,
  toNFZ3dFeatures,
  toNFZFeatures,
  toNIZFeatures,
} from '@components/map/layers/zones/zone.mapper';
import { selectActiveOrganization } from '@redux/authent/authent.base.selectors';
import {
  selectPopupsByMapId,
  selectSelectedExpiredDroneNotifAndDateByMapId,
  selectSelectedTargetIdsAndDatesByMapId,
} from '@redux/maps/maps.selectors';
import {
  selectActualMapFilters,
  selectHiddenFilterKeys,
  selectSpeedVectorLength,
} from '@redux/settings/settings.selectors';
import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectParameterWithReplayMode } from '@utils/common.utils';
import { isSolid3dPolygonValid } from '@utils/map/zone.utils';
import {
  getTargetClassification,
  getTargetIdentification,
  getTargetReliability,
  getTargetStatus,
  isDrone,
  isTracker,
} from '@utils/target.utils';
import getOutsideOfFlightPlansDrones from '@utils/utm/flightPlan.utils';
import {
  isValidCoordinates,
  isValidLinearLocation,
  isValidPolygon,
  isValidPolygonLocation,
  toLonLatArray,
} from '@utils/validation/coordinates.utils';
import { Feature, FeatureCollection } from 'geojson';
import { groupBy, uniqBy } from 'lodash';

import { NeutralInfo, TacticalSituation, Target, Track } from '@/types/c2/c2.types';
import {
  ConfigConfiguration,
  InitializationMaskingZone,
  NoFlyZone,
  NoFlyZone3d,
  NoInitZone,
} from '@/types/config/config.types';
import { PointOfInterest } from '@/types/config/pointOfInterest.types';
import { Round } from '@/types/config/round.types';
import {
  filterKeyToFeatureEnum,
  filterKeyToFilterTypeEnum,
  FilterTriggerEnum,
  filterTriggerToFilterKey,
  FilterTypeEnum,
  MapFilters,
} from '@/types/filters.types';
import { FeatureTypeEnum, MapIdEnum } from '@/types/map.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { IdentificationEnum } from '@/types/sensor/identification.types';
import {
  AbstractLocation,
  LocationTypeEnum,
  PlanarLocation,
  PolygonLocation,
  SpatialLocation,
  StrobeLocation,
  TargetLocation,
  TrackerLocation,
} from '@/types/sensor/sensor.types';
import { AbstractFlightPlan, FlightPlanActivationStateEnum } from '@/types/utm/flightPlan.types';

import { SituationState } from './situation.reducer';

function selectSituationState(state: RootState, replayMode = false): SituationState | null {
  if (replayMode) {
    return state.replay.situation;
  }
  return state.situation;
}

export const selectSituationVersion = createSelector(
  selectSituationState,
  (state: SituationState | null): number => state?.version ?? -1,
);

export function selectSituation(state: RootState): SituationState {
  return state.situation;
}

export const selectTacticalSituation = createSelector(
  selectSituationState,
  (state: SituationState | null): TacticalSituation | null => state?.tacticalSituation ?? null,
);

export const selectNeutralInfo = createSelector(
  [selectSituationState],
  (state: SituationState | null): NeutralInfo | null => state?.neutralInfo ?? null,
);

export const selectIsEngagementOnGoing = createSelector(
  selectNeutralInfo,
  (neutralInfo): boolean => !!neutralInfo?.targetId || !!neutralInfo?.coordinates,
);

export const selectEngagedTargetId = createSelector(
  selectNeutralInfo,
  (neutralInfo): string | null => neutralInfo?.targetId ?? null,
);

export const selectSituationFlightPlans = createSelector(
  selectSituationState,
  (state: SituationState | null): AbstractFlightPlan[] => state?.flightPlans ?? [],
);

export const selectSituationActiveFlightPlans = createSelector(
  selectSituationFlightPlans,
  (flightPlans: AbstractFlightPlan[]): AbstractFlightPlan[] =>
    flightPlans.filter((flightPlan) => flightPlan.activations[0].state === FlightPlanActivationStateEnum.ACTIVE),
);

export const selectSituationTime = createSelector(
  selectSituationState,
  (situation: SituationState | null): string | null => situation?.time ?? null,
);

export const selectFilteredFlightPlanFeatures = createSelector(
  [selectSituationFlightPlans, selectHiddenFilterKeys],
  (flightPlans, hiddenKeys): FeatureCollection => ({
    type: 'FeatureCollection',
    features: toFlightPlanFillFeatures(
      flightPlans.filter((fp) =>
        hiddenKeys.every(
          (key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.FLIGHT_PLAN) !== fp.state,
        ),
      ),
    ),
  }),
);

export const selectSituationFlightPlanById = createSelector(
  [selectSituationFlightPlans, selectParameterWithReplayMode<number>],
  (flightPlans, id): AbstractFlightPlan | null => flightPlans.find((flightPlan) => flightPlan.id === id) ?? null,
);

export const selectSituationFlightPlansByCodes = createSelector(
  [selectSituationFlightPlans, selectParameterWithReplayMode<string[]>],
  (flightPlans, codes): AbstractFlightPlan[] => flightPlans.filter((flightPlan) => codes.includes(flightPlan.code)),
);

export const selectConfigurations = createSelector(
  selectSituationState,
  (state: SituationState | null): ConfigConfiguration | null => state?.configuration ?? null,
);

export const selectTargets = createSelector(
  selectTacticalSituation,
  (state: TacticalSituation | null): Target<TargetLocation>[] =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    state?.targets.filter((target) => isValidCoordinates(target.lastPosition?.location?.position)) ?? [],
);

export const selectFilteredTargets = createSelector(
  [selectTargets, selectHiddenFilterKeys, selectActiveOrganization],
  (targets: Target<TargetLocation>[], hiddenKeys, organization): Target<TargetLocation>[] =>
    targets
      .filter((target) =>
        hiddenKeys.every((key) => {
          const filterType = filterKeyToFilterTypeEnum(key as keyof MapFilters);
          const feature = filterType && filterKeyToFeatureEnum(key as keyof MapFilters, filterType);
          switch (filterType) {
            case FilterTypeEnum.TARGET_TYPE:
              return feature !== getTargetIdentification(target, organization);
            case FilterTypeEnum.TARGET_CLASSIFICATION:
              return feature !== getTargetClassification(target, organization);
            case FilterTypeEnum.TARGET_RELIABILITY:
              return feature !== getTargetReliability(target);
            case FilterTypeEnum.TARGET_STATUS:
              return feature !== getTargetStatus(target);
            default:
              return true;
          }
        }),
      )
      .sort(
        (targetA, targetB) =>
          Object.keys(IdentificationEnum).indexOf(getTargetIdentification(targetB, organization)) -
          Object.keys(IdentificationEnum).indexOf(getTargetIdentification(targetA, organization)),
      ),
);

export const selectStrobes = createSelector(
  selectTacticalSituation,
  (state: TacticalSituation | null): Target<StrobeLocation>[] =>
    state?.strobes.filter((s) => isValidLinearLocation(s.lastPosition?.location)) ?? [],
);

export const selectFilteredStrobes = createSelector(
  [selectStrobes, selectHiddenFilterKeys, selectActiveOrganization],
  (strobes: Target<StrobeLocation>[], hiddenKeys, organization): Target<StrobeLocation>[] => {
    const hiddenRfFeatures = new Set(
      hiddenKeys
        .map((key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.RF_DETECTION_TYPE))
        .filter(Boolean),
    );
    const hiddenCuasFeatures = new Set(
      hiddenKeys
        .map((key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.C_UAS_SENSOR))
        .filter(Boolean),
    );

    return strobes.filter((strobe) => {
      if (hiddenRfFeatures.has(getTargetIdentification(strobe, organization))) {
        return false;
      }

      return strobe.trackIds.every((track) => !hiddenCuasFeatures.has(track.sensorType));
    });
  },
);

export const selectPolygons = createSelector(
  selectTacticalSituation,
  (state: TacticalSituation | null): Target<PolygonLocation>[] =>
    state?.polygons?.filter((polygon) => isValidPolygonLocation(polygon.lastPosition?.location)) ?? [],
);

export const selectFilteredPolygons = createSelector(
  [selectPolygons, selectHiddenFilterKeys, selectActiveOrganization],
  (polygons: Target<PolygonLocation>[], hiddenKeys, organization): Target<PolygonLocation>[] =>
    polygons.filter((polygon) =>
      hiddenKeys.every((key) => {
        const filterType = filterKeyToFilterTypeEnum(key as keyof MapFilters);
        const feature = filterType && filterKeyToFeatureEnum(key as keyof MapFilters, filterType);
        switch (filterType) {
          case FilterTypeEnum.TARGET_TYPE:
            return feature !== getTargetIdentification(polygon, organization);
          case FilterTypeEnum.TARGET_CLASSIFICATION:
            return feature !== getTargetClassification(polygon, organization);
          case FilterTypeEnum.TARGET_RELIABILITY:
            return feature !== getTargetReliability(polygon);
          case FilterTypeEnum.TARGET_STATUS:
            return feature !== getTargetStatus(polygon);
          default:
            return true;
        }
      }),
    ),
);

export const selectSpatialTargets = createSelector(
  selectTargets,
  (targets: Target<TargetLocation>[]): Target<SpatialLocation>[] =>
    targets.filter(
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (target) => target.lastPosition?.location?.type === LocationTypeEnum.SPATIAL,
    ) as Target<SpatialLocation>[],
);

export const selectDrones = createSelector(
  [selectFilteredTargets, selectActiveOrganization],
  (targets: Target<TargetLocation>[], organization): Target<TargetLocation>[] =>
    targets.filter((target) => isDrone(target, organization)),
);

export const selectSpatialDrones = createSelector(
  [selectSpatialTargets, selectActiveOrganization],
  (targets: Target<SpatialLocation>[], organization): Target<SpatialLocation>[] =>
    targets.filter((target) => isDrone(target, organization)),
);

export const selectGroupedTargets = createSelector(
  selectFilteredTargets,
  (targets): [Target<TargetLocation>, Target<TargetLocation>][] => {
    const targetsWithGroup = targets.filter((target) => target.targetGroup);
    return Object.values(groupBy(targetsWithGroup, 'targetGroup'))
      .filter((targets) => targets.length === 2)
      .map((targets) => [targets[0], targets[1]] as [Target<PlanarLocation>, Target<PlanarLocation>]);
  },
);

export const selectTargetIconFeatures = createSelector(
  [selectFilteredTargets, selectActiveOrganization],
  (targets: Target<TargetLocation>[], organization: string | null): Feature[] =>
    toTargetIconFeatures(targets, organization),
);

export const selectTargetSpeedVectorFeatures = createSelector(
  [selectFilteredTargets, selectActualMapFilters, selectSpeedVectorLength, selectActiveOrganization],
  (
    targets: Target<TargetLocation>[],
    mapFilters: MapFilters,
    speedVectorLength: number,
    organization: string | null,
  ): Feature[] =>
    mapFilters.displaySpeedVector ? toTargetSpeedFeatures(targets, speedVectorLength, organization) : [],
);

export const selectTargetLinkFeatures = createSelector(
  [selectGroupedTargets, selectActiveOrganization],
  (targets: [Target<TargetLocation>, Target<TargetLocation>][], organization: string | null): Feature[] =>
    toTargetLinkFeatures(targets, organization),
);

export const selectTargetFlightPlanLinkFeatures = createSelector(
  [selectDrones, selectSituationActiveFlightPlans],
  (droneTargets, flightPlans): Feature[] =>
    toTargetFlightPlansLinkFeatures(getOutsideOfFlightPlansDrones(droneTargets, flightPlans)),
);

export const selectSelectedTargets = createSelector(
  [
    (state, mapId: MapIdEnum) => selectTargets(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectSelectedTargetIdsAndDatesByMapId(state, mapId),
  ],
  (targets: Target<TargetLocation>[], selectedTargetIdsAndDates): Target<TargetLocation>[] =>
    targets.filter((target) => selectedTargetIdsAndDates.some((val) => val.targetId === target.id)),
);

export const selectSelectedTargetsAndDates = createSelector(
  [
    (state, mapId: MapIdEnum) => selectTargets(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectSelectedTargetIdsAndDatesByMapId(state, mapId),
    (state, mapId: MapIdEnum) => selectNeutralInfo(state, mapId === MapIdEnum.REPLAY),
  ],
  (
    targets: Target<TargetLocation>[],
    selectedTargetIdsAndDates,
  ): { selectedTarget: Target<TargetLocation>; openingDate: Date }[] =>
    selectedTargetIdsAndDates
      .map((val) => {
        const selectedTarget = targets.find((target) => val.targetId === target.id) ?? null;
        return selectedTarget ? { selectedTarget, openingDate: val.openingDate } : null;
      })
      .filter(Boolean) as { selectedTarget: Target<TargetLocation>; openingDate: Date }[],
);

export const selectFilteredTargetForTrajectory = createSelector(
  [
    (state, mapId: MapIdEnum) => selectFilteredTargets(state, mapId === MapIdEnum.REPLAY),
    selectHiddenFilterKeys,
    selectSelectedTargets,
    selectActiveOrganization,
  ],
  (filteredTargets: Target<TargetLocation>[], hiddenKeys, selectedTargets, organization): Target<TargetLocation>[] => {
    // TANT QUE LA CIBLE SELECTIONNEE NE PREND PAS EN COMPTE LES FILTRES
    const targets =
      selectedTargets.length !== 0
        ? uniqBy([...filteredTargets, ...selectedTargets], (target) => target.id)
        : filteredTargets;

    const hiddenKeySet = new Set(hiddenKeys);
    const allowPinnedTrajectory = !hiddenKeySet.has(
      filterTriggerToFilterKey[FilterTypeEnum.TARGET_TRAJECTORY][FilterTriggerEnum.TARGET_TRAJECTORY_SELECTED]!,
    );
    const hiddenTrajectoryFeatures = new Set(
      hiddenKeys
        .filter((key) => filterKeyToFilterTypeEnum(key as keyof MapFilters) === FilterTypeEnum.TARGET_TRAJECTORY)
        .map((key) => filterKeyToFeatureEnum(key as keyof MapFilters, FilterTypeEnum.TARGET_TRAJECTORY))
        .filter(Boolean)
        .map((feature) => feature!.split('_').slice(1).join('_')),
    );

    return targets.filter((target) => {
      const identification = getTargetIdentification(target, organization);
      const isHidden = hiddenTrajectoryFeatures.has(identification);

      if (!isHidden) {
        return true;
      }

      return allowPinnedTrajectory && selectedTargets.some((val) => val.id === target.id);
    });
  },
);

export const selectTargetTrajectoryFeatures = createSelector(
  [selectFilteredTargetForTrajectory, selectActiveOrganization],
  (targets: Target<TargetLocation>[], organization: string | null): Feature[] =>
    toTargetTrajectoryFeatures(targets, organization),
);

export const selectGroupedTargetWithSelectedTarget = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectTargets(state, replayMode),
    (state: RootState, replayMode: boolean) => selectStrobes(state, replayMode),
    (_state: RootState, _replayMode: boolean, selectedTarget: Target<AbstractLocation> | null) => selectedTarget,
  ],
  (targets, strobes, selectedTarget) => {
    if (!selectedTarget?.targetGroup) return null;

    return (
      [...targets, ...strobes].find(
        (target) => target.targetGroup === selectedTarget.targetGroup && target.id !== selectedTarget.id,
      ) ?? null
    );
  },
);

export const selectGroupedPolygonWithSelectedPolygon = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectTargets(state, replayMode),
    (state: RootState, replayMode: boolean) => selectStrobes(state, replayMode),
    (state: RootState, replayMode: boolean) => selectPolygons(state, replayMode),
    (_state: RootState, _replayMode: boolean, selectedPolygon: Target<PolygonLocation> | null) => selectedPolygon,
  ],
  (targets, strobes, polygons, selectedPolygon) => {
    if (!selectedPolygon?.targetGroup) return null;

    return (
      [...targets, ...strobes, ...polygons].find(
        (target) => target.targetGroup === selectedPolygon.targetGroup && target.id !== selectedPolygon.id,
      ) ?? null
    );
  },
);

export const selectStrobeById = createSelector(
  [selectStrobes, selectParameterWithReplayMode<string>],
  (strobes, strobeId) => strobes.find((strobe) => strobe.id === strobeId),
);

export const selectPolygonById = createSelector(
  [selectPolygons, selectParameterWithReplayMode<string>],
  (polygons, polygonId) => polygons.find((polygon) => polygon.id === polygonId),
);

export const selectSelectedTargetCrossFeatures = createSelector(
  [selectSelectedTargetsAndDates],
  (selectedTargetsAndDates): Feature[] =>
    selectedTargetsAndDates.flatMap((val) => {
      const targetPosition = toLonLatArray(val.selectedTarget.lastPosition.location!.position, false)!;
      return toTargetCrossFeature(targetPosition, val.openingDate);
    }),
);

export const selectIsEngagedTargetById = createSelector(
  [selectEngagedTargetId, selectParameterWithReplayMode<string>],
  (engagedTargetId, targetId): boolean => engagedTargetId === targetId,
);

export const selectSelectedEngagementFeatures = createSelector(
  [
    selectSelectedTargets,
    (state, mapId: MapIdEnum) => selectNeutralInfo(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectPopupsByMapId(state, mapId),
    selectActiveOrganization,
  ],
  (selectedTargets, neutralInfo, popups, organization: string | null): Feature[] => {
    const selectedEngagedTarget = selectedTargets.find(
      (target) => (target.displayId ?? target.id) === neutralInfo?.targetId,
    );
    if (selectedEngagedTarget) {
      return [
        toEngagementCircleFeature(selectedEngagedTarget.lastPosition.location!.position!),
        toEngagedTargetIconFeature(selectedEngagedTarget, organization),
      ];
    } else if (popups.some((popup) => popup.type === PopupTypeEnum.ENGAGEMENT) && neutralInfo?.coordinates) {
      return [toEngagementCircleFeature(neutralInfo.coordinates)];
    }
    return [];
  },
);

export const selectSelectedTargetFeatures = createSelector(
  [
    selectSelectedTargetsAndDates,
    selectSelectedTargetCrossFeatures,
    selectSelectedEngagementFeatures,
    (state, mapId: MapIdEnum) => selectNeutralInfo(state, mapId === MapIdEnum.REPLAY),
    selectActiveOrganization,
  ],
  (
    selectedTargetsAndDates,
    selectedTargetCrossFeatures,
    selectedTargetEngagementFeatures,
    neutralInfo,
    organization: string | null,
  ): Feature[] => [
    ...toTargetIconFeatures(
      selectedTargetsAndDates
        .filter((val) => val.selectedTarget.displayId !== neutralInfo?.targetId)
        .map((val) => val.selectedTarget),
      organization,
    ),
    ...selectedTargetCrossFeatures,
    ...selectedTargetEngagementFeatures,
  ],
);

export const selectSelectedExpiredDroneCrossFeature = createSelector(
  (state, mapId) => selectSelectedExpiredDroneNotifAndDateByMapId(state, mapId),
  (selectSelectedExpiredDroneNotifAndDate): Feature[] => {
    if (!selectSelectedExpiredDroneNotifAndDate) {
      return [];
    }
    const notificationPosition = toLonLatArray(selectSelectedExpiredDroneNotifAndDate.notification.position, false)!;
    return toTargetCrossFeature(notificationPosition, selectSelectedExpiredDroneNotifAndDate.openingDate);
  },
);

export const selectSelectedExpiredDroneTrajectoryFeatures = createSelector(
  [(state, mapId) => selectSelectedExpiredDroneNotifAndDateByMapId(state, mapId), selectActualMapFilters],
  (selectedExpiredDroneNotifAndDate, mapFilters): Feature[] => {
    if (
      !selectedExpiredDroneNotifAndDate ||
      (!mapFilters.displayTrajectoryDrone && !mapFilters.displayTrajectoryPinnedTarget)
    ) {
      return [];
    }
    return toExpiredDroneTrajectoryFeatures(selectedExpiredDroneNotifAndDate.notification);
  },
);

export const selectSelectedExpiredDroneFeatures = createSelector(
  [
    (state, mapId) => selectSelectedExpiredDroneNotifAndDateByMapId(state, mapId),
    selectSelectedExpiredDroneCrossFeature,
    selectSelectedExpiredDroneTrajectoryFeatures,
  ],
  (
    selectedExpiredDroneNotifAndDate,
    selectedExpiredDroneCrossFeature,
    selectedExpiredDroneTrajectoryFeatures,
  ): Feature[] =>
    (selectedExpiredDroneNotifAndDate && [
      toExpiredDroneIconFeature(selectedExpiredDroneNotifAndDate.notification),
      ...selectedExpiredDroneCrossFeature,
      ...selectedExpiredDroneTrajectoryFeatures,
    ]) ??
    [],
);

export const selectTargetFeatures = createSelector(
  [
    (state, mapId: MapIdEnum) => selectTargetIconFeatures(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectTargetLinkFeatures(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectTargetSpeedVectorFeatures(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectTargetFlightPlanLinkFeatures(state, mapId === MapIdEnum.REPLAY),
    selectTargetTrajectoryFeatures,
  ],
  (
    targetIconFeatures,
    targetLinkFeatures,
    targetSpeedVectorFeatures,
    targetFlightPlanLinkFeatures,
    targetTrajectoryFeatures,
  ): FeatureCollection => ({
    type: 'FeatureCollection',
    features: [
      ...targetIconFeatures,
      ...targetLinkFeatures,
      ...targetSpeedVectorFeatures,
      ...targetTrajectoryFeatures,
      ...targetFlightPlanLinkFeatures,
    ],
  }),
);

export const selectEngagementFeatures = createSelector(
  [
    (state, mapId: MapIdEnum) => selectNeutralInfo(state, mapId === MapIdEnum.REPLAY),
    (state, mapId: MapIdEnum) => selectTargets(state, mapId === MapIdEnum.REPLAY),
    selectActiveOrganization,
  ],
  (neutralInfo, targets, organization: string | null): FeatureCollection => {
    if (neutralInfo?.targetId) {
      const target = targets.find((target) => (target.displayId ?? target.id) === neutralInfo.targetId);
      return {
        type: 'FeatureCollection',
        features: target
          ? [
              toEngagedTargetIconFeature(target, organization),
              toEngagementCircleFeature(target.lastPosition.location!.position!),
            ]
          : [toEngagementCircleFeature(neutralInfo.coordinates)],
      };
    } else if (neutralInfo?.coordinates) {
      return {
        type: 'FeatureCollection',
        features: [toEngagementCircleFeature(neutralInfo.coordinates)],
      };
    }
    return {
      type: 'FeatureCollection',
      features: [],
    };
  },
);

export const selectStrobeSectorFeatures = createSelector(
  selectFilteredStrobes,
  (strobes: Target<StrobeLocation>[]): Feature[] => toStrobeSectorFeatures(strobes),
);

export const selectStrobeAzimuthLineFeatures = createSelector(
  selectFilteredStrobes,
  (strobes: Target<StrobeLocation>[]): Feature[] => toStrobeAzimuthLineFeatures(strobes),
);

export const selectPolygonLocationFeatures = createSelector(
  selectFilteredPolygons,
  (polygons: Target<PolygonLocation>[]): Feature[] => toPolygonLocationFeatures(polygons),
);

export const selectPolygonTrajectoryOutlineFeatures = createSelector(
  selectFilteredPolygons,
  (polygons: Target<PolygonLocation>[]): Feature[] => toPolygonTrajectoryOutlineFeatures(polygons),
);

export const selectPolygonLatestOutlineFeatures = createSelector(
  selectFilteredPolygons,
  (polygons: Target<PolygonLocation>[]): Feature[] => toPolygonLatestOutlineFeatures(polygons),
);

export const selectPolygonFeatures = createSelector(
  [selectPolygonLocationFeatures, selectPolygonTrajectoryOutlineFeatures, selectPolygonLatestOutlineFeatures],
  (
    polygonLocations: Feature[],
    polygonTrajectoryOutlines: Feature[],
    polygonLatestOutlines: Feature[],
  ): FeatureCollection => ({
    type: 'FeatureCollection',
    features: [...polygonTrajectoryOutlines, ...polygonLocations, ...polygonLatestOutlines],
  }),
);

export const selectStrobeFeatures = createSelector(
  [selectStrobeSectorFeatures, selectStrobeAzimuthLineFeatures],
  (sectors: Feature[], azimuthLines: Feature[]): FeatureCollection => ({
    type: 'FeatureCollection',
    features: [...sectors, ...azimuthLines],
  }),
);

export const selectTargetById = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectTargets(state, replayMode),
    (_state: RootState, _replayMode: boolean, targetId: string) => targetId,
  ],
  (targets, targetId): Target<TargetLocation> | undefined => targets.find((target) => target.id === targetId),
);

export const selectTargetByDisplayId = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectTargets(state, replayMode),
    (_state: RootState, _replayMode: boolean, targetId: string) => targetId,
  ],
  (targets, targetId): Target<TargetLocation> | undefined => targets.find((target) => target.displayId === targetId),
);

export const selectAllNoInitZones = createSelector(
  selectConfigurations,
  (config: ConfigConfiguration | null): NoInitZone[] => config?.noInitZones ?? [],
);

export const selectAllValidNoInitZone = createSelector(selectAllNoInitZones, (nizs: NoInitZone[]): NoInitZone[] =>
  nizs.filter((niz: NoInitZone) => isValidPolygon(niz.polygon)),
);

const selectFilteredNoInitZones = createSelector(
  [selectAllValidNoInitZone, selectHiddenFilterKeys],
  (nizs, hiddenKeys) => {
    const key = filterTriggerToFilterKey[FilterTypeEnum.ZONE][FeatureTypeEnum.NO_INIT_ZONE];
    if (!key) {
      return nizs;
    }
    return hiddenKeys.includes(key) ? [] : nizs;
  },
);

export const selectAllNoInitZoneFeatures = createSelector(selectFilteredNoInitZones, (nizs: NoInitZone[]): Feature[] =>
  toNIZFeatures(nizs),
);

export const selectAllNoFlyZones = createSelector(
  selectConfigurations,
  (config: ConfigConfiguration | null): NoFlyZone[] => config?.noFlyZones ?? [],
);

export const selectAllValidNoFlyZone = createSelector(selectAllNoFlyZones, (nfzs: NoFlyZone[]): NoFlyZone[] =>
  nfzs.filter((nfz: NoFlyZone) => nfz.active && isValidPolygon(nfz.polygon)),
);

const selectFilteredNoFlyZones = createSelector(
  [selectAllValidNoFlyZone, selectHiddenFilterKeys],
  (nfzs, hiddenKeys) => {
    if (!hiddenKeys.length) {
      return nfzs;
    }
    const hiddenKeysSet = new Set(hiddenKeys);

    return nfzs.filter((nfz) => {
      const key = filterTriggerToFilterKey[FilterTypeEnum.ZONE][nfz.level];
      if (!key) return true;
      return !hiddenKeysSet.has(key);
    });
  },
);

export const selectAllNoFlyZoneFeatures = createSelector(selectFilteredNoFlyZones, (nfzs: NoFlyZone[]): Feature[] =>
  toNFZFeatures(nfzs),
);

export const selectAllNoFlyZones3d = createSelector(
  selectConfigurations,
  (config: ConfigConfiguration | null): NoFlyZone3d[] => config?.noFlyZones3d ?? [],
);

export const selectAllValidNoFlyZone3d = createSelector(selectAllNoFlyZones3d, (nfzs: NoFlyZone3d[]): NoFlyZone3d[] =>
  nfzs.filter((nfz: NoFlyZone3d) => nfz.active && isSolid3dPolygonValid(nfz.solidPolygon)),
);

const selectFilteredNoFly3dZones = createSelector(
  [selectAllValidNoFlyZone3d, selectHiddenFilterKeys],
  (nfzs, hiddenKeys) => {
    if (!hiddenKeys.length) {
      return nfzs;
    }
    const hiddenKeysSet = new Set(hiddenKeys);

    return nfzs.filter((nfz) => {
      const key = filterTriggerToFilterKey[FilterTypeEnum.ZONE][nfz.level];
      if (!key) return true;
      return !hiddenKeysSet.has(key);
    });
  },
);

export const selectAllNoFlyZone3dFeatures = createSelector(
  selectFilteredNoFly3dZones,
  (nfzs: NoFlyZone3d[]): Feature[] => toNFZ3dFeatures(nfzs),
);

export const selectAllInitializationMaskingZones = createSelector(
  selectConfigurations,
  (config: ConfigConfiguration | null): InitializationMaskingZone[] => config?.initializationMaskingZones ?? [],
);

export const selectAllValidInitializationMaskingZone = createSelector(
  selectAllInitializationMaskingZones,
  (imzs: InitializationMaskingZone[]): InitializationMaskingZone[] =>
    imzs.filter((imz: InitializationMaskingZone) => imz.active && isValidPolygon(imz.polygon)),
);

const selectFilteredInitializationMaskingZones = createSelector(
  [selectAllValidInitializationMaskingZone, selectHiddenFilterKeys],
  (imzs, hiddenKeys) => {
    const key = filterTriggerToFilterKey[FilterTypeEnum.ZONE][FeatureTypeEnum.INITIALIZATION_MASKING_ZONE];
    if (!key) {
      return imzs;
    }
    return hiddenKeys.includes(key) ? [] : imzs;
  },
);

export const selectAllInitializationMaskingZoneFeatures = createSelector(
  selectFilteredInitializationMaskingZones,
  (imzs: InitializationMaskingZone[]): Feature[] => [...toIMZFeatures(imzs), ...toIMZMarginFeatures(imzs)],
);

export const selectSitacZonesFilteredFeatures = createSelector(
  [
    selectAllNoInitZoneFeatures,
    selectAllNoFlyZoneFeatures,
    selectAllInitializationMaskingZoneFeatures,
    selectAllNoFlyZone3dFeatures,
  ],
  (nizFeatures: Feature[], nfzFeatures: Feature[], imzFeatures: Feature[], nfz3dFeatures: Feature[]): Feature[] => [
    ...nizFeatures,
    ...nfzFeatures,
    ...imzFeatures,
    ...nfz3dFeatures,
  ],
);

export const selectPointOfInterests = createSelector(
  selectConfigurations,
  (configuration) => configuration?.pointOfInterests ?? [],
);

export const selectFilteredPointOfInterests = createSelector(
  [selectPointOfInterests, selectActualMapFilters],
  (pois, mapFilters): PointOfInterest[] => (mapFilters.displayOtherInfoPoi ? pois : []),
);

export const selectPointOfInterestFeatures = createSelector(
  selectFilteredPointOfInterests,
  (pois): FeatureCollection => ({
    type: 'FeatureCollection',
    features: toActivePoiFeatures(pois),
  }),
);

export const selectRounds = createSelector(
  selectSituationState,
  (state: SituationState | null): Round[] => state?.rounds ?? [],
);

export const selectTrackFromRawSituationById = createSelector(
  [
    (state: RootState, replayMode: boolean) => selectSituationState(state, replayMode),
    (_state: RootState, _replayMode: boolean, trackId: string) => trackId,
  ],
  (situation, trackId) =>
    situation?.rawSituation?.tracks.find((track: Track<AbstractLocation>) => track.id === trackId) ?? null,
);

export const selectTrackLastPositionFromRawSituationByUniqueKey = createSelector(
  [selectSituationState, selectParameterWithReplayMode<string | null>],
  (situation, uniqueKey): TrackerLocation[] =>
    situation?.rawSituation?.tracks
      .filter(isTracker)
      .filter((track) => track.uniqueKey === uniqueKey)
      .map((track) => track.lastPosition!.location!) ?? [],
);
